/**
 * Campaign registry.
 *
 * Each product recall campaign is described here. The active campaign is
 * selected at build time through the `CAMPAIGN` environment variable
 * (inlined by Parcel), so each build/domain ships a single campaign.
 */
import type React from 'react'
import sd500Locales from '../../../locales/sd500-emd500e'
import simondLocales from '../../../locales/simond-quickdraw'
//@ts-expect-error - image asset import handled by Parcel
import defaultLogo from '../assets/images/logo.png'
//@ts-expect-error - image asset import handled by Parcel
import simondLogo from '../assets/images/simond-logo.png'
import { activeCampaignSlug } from './active'
import type { QuantityStepProps } from '../components/form/quantity-step-props'
import Sd500QuantityForm from '../components/form/sd500-quantity-form'

/**
 * Assets and links used by the repair presentation step.
 */
export interface RepairConfig {
  /** Public URL of the downloadable repair guide (PDF). Empty if not available yet. */
  pdfUrl: string
  /** Embeddable tutorial video URL. Empty if not available yet. */
  videoUrl: string
  /**
   * Base name of the repair kit image (served from /assets/images/optimized).
   * Leave undefined to hide the image when no campaign-specific visual exists.
   */
  kitImageName?: string
  /**
   * Whether to display the warranty notice below the call to action.
   * Some campaigns have no `repair.warranty` translation, so it is opt-in.
   */
  showWarranty?: boolean
}

/**
 * TODO (archi, à reconsidérer dès la 3e campagne ou si les jeux de steps divergent) :
 * Le wizard est aujourd'hui un moteur partagé + des points d'extension injectés
 * par campagne (QuantityForm, isConcerned, repair, ...). Si les différences se
 * multiplient, migrer vers un wizard "piloté par config" : chaque campagne
 * déclare une liste ordonnée de steps (id, Component, titleKey, next/branchement)
 * consommée par un moteur générique. Cela évite à la fois la "config soup"
 * (injection fine-grain) et la duplication d'un composant-formulaire complet par
 * campagne. Voir discussion : flows B900/SD500 ~95% identiques aujourd'hui.
 */

/**
 * Configuration for a single recall campaign.
 */
export interface CampaignConfig {
  /** Unique campaign identifier */
  slug: string
  /** Translations used by the presentation (home) page */
  presentationLocales: Record<string, any>
  /** Translations used by the form (wizard) page */
  formLocales: Record<string, any>
  /** Logo image (bundled asset) displayed at the top of the pages */
  logo: string
  /** Base name of the product image served from /assets/images/optimized */
  productImageName: string
  /** Alt text for the product image */
  productImageAlt: string
  /**
   * Campaign-specific quantity step component.
   * Wizard-only: campaigns that ship their own screens omit it.
   */
  QuantityForm?: React.FC<QuantityStepProps>
  /**
   * Decides, from the created quantity record, whether the requestor is
   * concerned (eligible for the repair kit) or not.
   * Wizard-only.
   */
  isConcerned?: (quantity: Record<string, number>) => boolean
  /**
   * Product quantity lines shown in the kit-request confirmation modal
   * (quantity API key + translation key for the product label).
   * Wizard-only.
   */
  quantityConfirmationFields?: { quantityKey: string; labelKey: string }[]
  /**
   * Repair presentation assets/links.
   * Wizard-only: campaigns whose remedy is not a repair kit omit it.
   */
  repair?: RepairConfig
  /** Show a phone contact button (opens a contact modal) in the header */
  hasContactButton: boolean
  /**
   * When true, the kit request is saved as a draft on the delivery form step
   * and confirmed on the dedicated confirmation step (SD500 / EMD500E only).
   */
  kitRequestDraftFlow?: boolean
}

/**
 * All known campaigns, keyed by slug.
 */
const campaigns: Record<string, CampaignConfig> = {
  'sd500-emd500e': {
    slug: 'sd500-emd500e',
    // This campaign stores all translations in a single sheet, so the same
    // locales object is used for both the presentation and the form.
    presentationLocales: sd500Locales,
    formLocales: sd500Locales,
    // TODO: replace with a dedicated logo asset if provided
    logo: defaultLogo,
    // TODO: confirm the real optimized asset base name for this product
    productImageName: 'sd500_emd500e',
    productImageAlt: 'Decathlon SD500 / EMD500E',
    QuantityForm: Sd500QuantityForm,
    // At least one product owned means the requestor is concerned.
    isConcerned: quantity => (quantity?.sd500 ?? 0) + (quantity?.emd500e ?? 0) > 0,
    quantityConfirmationFields: [
      { quantityKey: 'sd500', labelKey: 'recall.descriptionSd500' },
      { quantityKey: 'emd500e', labelKey: 'recall.descriptionEmd500e' },
    ],
    repair: {
      pdfUrl: '/assets/pdf/NOTICE_A4.pdf',
      videoUrl: 'https://www.youtube.com/embed/ZWk3vYHCto8?rel=0&modestbranding=1',
      kitImageName: 'sd500_repair_kit',
      showWarranty: false,
    },
    hasContactButton: true,
    kitRequestDraftFlow: true,
  },
  /**
   * Simond Alpinism / Vertika quickdraws.
   *
   * Ships its own screens rather than the shared wizard: the remedy is a store
   * return and refund, so there is no quantity step, no repair kit and no
   * delivery form. Only the branding and locale fields apply.
   */
  'simond-quickdraw': {
    slug: 'simond-quickdraw',
    // One sheet holds every block, so presentation and form share it.
    presentationLocales: simondLocales,
    formLocales: simondLocales,
    logo: simondLogo,
    productImageName: 'simond_quickdraw',
    productImageAlt: 'Simond Alpinism / Vertika quickdraws',
    // Support is offered through the phone number inside the outcome copy,
    // not through the header button.
    hasContactButton: false,
  },
}

/**
 * The active campaign configuration for this build.
 *
 * Fails fast when CAMPAIGN is missing or unknown: there must always be a valid
 * active campaign, and silently falling back would risk serving the wrong
 * product on a domain.
 */
if (!activeCampaignSlug || !campaigns[activeCampaignSlug]) {
  throw new Error(
    `CAMPAIGN is required and must be one of: ${Object.keys(campaigns).join(', ')} (got "${
      activeCampaignSlug ?? 'undefined'
    }")`,
  )
}

export const campaign: CampaignConfig = campaigns[activeCampaignSlug]
